<i18n>
  en:
    Firstname: Firstname
    Lastname: Lastname
    Email: Email
    Password: Password
    Continue to payment: Continue to payment
    loading: ...loading
    emailShouldBeUnique: This e-mail address is already taken.
    uniqueNickname: Unfortunately, this nickname is already taken.
    legalAccountCreation: By creating your account, you accept our <a class="underline text-primary-500 hover:text-primary-600" href="/portal/terms" target="_blank">terms and conditions</a>.
    pleaseFillOutAllFieldsHint: Please fill out all fields. After completing the payment, you will receive an email with all the information about your purchase.
    uniqueNicknameHint: This will be your unique getnext account name, a pseudonym if you do not want to use your real name.
    passwordHint: Please enter at least eight characters, including letters (upper and lower case), numbers, and special characters.
    accountCreationHint1: To facilitate the purchase and to release further content on
    accountCreationHint2: a user account is created with the order.
    You are not signed in: You are not signed in.
    Please: Please
    sign in: sign in
    if you already have an account: if you already have an account.

  de:
    Firstname: Vorname
    Lastname: Nachname
    Email: Email
    Password: Passwort
    Continue to payment: Weiter zur Zahlung
    loading: ...laden
    emailShouldBeUnique: Diese E-Mail-Adresse ist schon vergeben.
    uniqueNickname: Dieser Nickname ist leider schon vergeben.
    legalAccountCreation: Für die Nutzung der Plattform gelten die <a class="underline text-primary-500 hover:text-primary-600" href="/portal/agb" target="_blank">Allgemeinen Nutzungsbedingungen</a>.
    pleaseFillOutAllFieldsHint: Bitte fülle alle Felder aus. Nach Abschluss der Bezahlung, erhältst Du eine Email mit allen Infos zu deinem Kauf.
    uniqueNicknameHint: Das wird Dein eindeutiger getnext Kontoname, ein Pseudonym, falls Du nicht deinen echten Namen verwenden willst.
    passwordHint: Bitte gib mindestens acht Zeichen ein, es sollten Buchstaben (Groß- und Kleinschreibung), Zahlen und Sonderzeichen enthalten sein.
    accountCreationHint1: Damit der Kauf und die Freigabe weiterer Inhalte auf
    accountCreationHint2: erfolgen können, wird mit der Bestellung ein Nutzerkonto erstellt.
    You are not signed in: Du bist nicht eingeloggt.
    Please: Bitte
    sign in: logge Dich ein
    if you already have an account: falls Du bereits ein Konto hast.

</i18n>

<template>
  <FormulateForm
    @submit="registerNow"
    ref="form"
    name="registerForm"
    class="text-left space-y-2"
  >
    <p class="pb-5">
      {{ $t('You are not signed in') }}
      {{ $t('Please') }}
      <a
        class="underline cursor-pointer text-primary-600 hover:text-primary-500"
        :href="loginPath"
      >
        <span>{{ $t('sign in') }}</span>
      </a>
      <span>,</span>
      {{ $t('if you already have an account') }}
    </p>

    <FormulateInput
      :label="$t('Nickname')"
      :placeholder="$t('Wähle einen Nickname')"
      type="text"
      v-model="nickname"
      autocomplete="name"
      validation-name="Nickname"
      :validation="`bail|required|uniqueNickname`"
      :validation-rules="{
        uniqueNickname: uniqueNickname
      }"
      :validation-messages="vMessages"
    />
    <p class="text-xs text-gray-400 pb-5">
      {{ $t('uniqueNicknameHint') }}
    </p>

    <FormulateInput
      class="pb-4"
      :label="$t('Email')"
      :placeholder="$t('Deine Email')"
      type="email"
      required
      autocomplete="email"
      v-model="email"
      :validation="`bail|required|email|uniqueEmail`"
      :validation-rules="{
        uniqueEmail: uniqueEmail
      }"
      :validation-messages="vMessages"
    />

    <FormulateInput
      :label="$t('Password')"
      :placeholder="$t('Wähle ein Passwort')"
      autocomplete="new-password"
      type="password"
      v-model="password"
      :validation="[
        'bail',
        'required',
        'min:8,length',
        ['matches', /(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]/]
      ]"
    />
    <p class="text-xs text-gray-400 pb-5">
      {{ $t('passwordHint') }}
    </p>

    <withdrawal-waiver
      v-if="redirectToCheckout"
      :grantWithdrawalRight="!!grantWithdrawalRight"
      @change="acceptedWaiver"
      class="pb-5"
    ></withdrawal-waiver>

    <SubmitButton
      :isLoading="isLoading"
      :hasErrors="hasErrors"
      :disabled="buttonDisabled"
    >
      <template #submit>
        {{ $t('Continue to payment') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>
    </SubmitButton>

    <p class="text-sm text-gray-500 pt-2">
      {{ $t('accountCreationHint1') }}
      <a class="text-primary-500" :href="`/${urlAlias}`" target="_blank">
        getnext.to/{{ sellerName }},
      </a>
      {{ $t('accountCreationHint2') }}
    </p>
    <p class="text-sm text-gray-500" v-html="$t('legalAccountCreation')"></p>
  </FormulateForm>
</template>

<style scoped>
.btn {
  @apply shadow w-full flex items-center justify-center px-5 py-3 border border-transparent text-base leading-6 font-medium rounded-md transition duration-150 ease-in-out;
}
</style>

<script>
/* global Stripe */
import SubmitButton from '@/components/submitButton.vue'
import WithdrawalWaiver from '@/components/withdrawalWaiver.vue'
import { call, sync, get } from 'vuex-pathify'

export default {
  components: {
    SubmitButton,
    WithdrawalWaiver
  },

  props: {
    rewardId: String,
    customAmount: Number,
    afterSaleAnswer: String,
    redirectToCheckout: Boolean,
    grantWithdrawalRight: Boolean
  },

  data() {
    return {
      nickname: null,
      email: null,
      password: null,

      isLoading: false,
      hasErrors: false,
      waiverAccepted: false
    }
  },

  computed: {
    publishableKey: sync('checkout/publishableKey'),
    sellerName: get('reward/nickname'),
    urlAlias: get('reward/urlAlias'),

    buttonDisabled() {
      return (
        this.redirectToCheckout &&
        !this.grantWithdrawalRight &&
        !this.waiverAccepted
      )
    },

    vMessages() {
      return (
        this.$i18n.locale && {
          uniqueEmail: this.$t('emailShouldBeUnique'),
          uniqueNickname: this.$t('uniqueNickname')
        }
      )
    },

    loginPath() {
      const path = window.location.pathname
      return this.$i18n.locale === 'en'
        ? `/portal/login?redirect=${path}`
        : `/portal/einloggen?redirect=${path}`
    }
  },

  methods: {
    checkUniqueEmail: call('user/uniqueEmail'),
    checkUniqueNickname: call('user/uniqueNickname'),
    registerUser: call('user/registerUser'),
    loginUser: call('user/loginUser'),
    startSession: call('checkout/startSession'),

    /**
     * await required by formulate
     */
    async uniqueEmail({ value }) {
      if (value) {
        return await this.checkUniqueEmail(value)
      } else {
        return true
      }
    },

    /**
     * await required by formulate
     */
    async uniqueNickname({ value }) {
      if (value) {
        return await this.checkUniqueNickname(value)
      } else {
        return true
      }
    },

    async registerNow() {
      this.isLoading = true
      const { email, password, nickname } = this

      await this.registerUser({
        email,
        password,
        nickname
      })

      const { user } = await this.loginUser({ email, password })

      if (this.redirectToCheckout) {
        await this.gotToPayment(user.id)
      } else {
        this.isLoading = false
        this.$emit('registered')
      }
    },

    async gotToPayment(buyerId) {
      if (!this.$stripe) {
        this.$stripe = Stripe(this.publishableKey)
      }

      const session = await this.startSession({
        buyerId,
        itemId: this.rewardId,
        customAmount: this.customAmount,
        afterSaleAnswer: this.afterSaleAnswer
      })

      if (session && session.sessionId) {
        await this.$stripe.redirectToCheckout(session)
      }

      if (session && session.code === 666) {
        this.$router.push({
          name: 'soldOut',
          params: { rewardId: this.rewardId, sellerId: this.sellerId }
        })
      }
    },
    acceptedWaiver(val) {
      this.waiverAccepted = val
    }
  }
}
</script>
